import React, { useState, useEffect } from 'react';
import { DatePicker } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import { fetchRooms } from '../../state/actions/room';
import { setActiveTab } from '../../state/actions/tab';
import { setBreadcrumb } from 'state/actions/breadcrumb';
import VirtualCalendarWeekly from '../../components/VirtualCalendar/VirtualCalendarWeekly';
import moment from 'moment-timezone';
import dayjs from 'dayjs';
import './RoomDetail.css';

const RoomDetail = () => {
  const dispatch = useDispatch();
  const { siteId, siteName, roomId } = useParams();
  const [search, setSearch] = useState('');
  const [searchError, setSearchError] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());

  const { rooms, loading, activeTab } = useSelector((state) => ({
    rooms: state.room?.rooms || [],
    loading: state.room?.loading || false,
    activeTab: state.tab?.tab,
  }));

  // Set active tab when component mounts
  useEffect(() => {
    dispatch(setActiveTab({ tab: 'roomManagement' }));
  }, [dispatch]);

  // Fetch rooms when component mounts or tab becomes active
  useEffect(() => {
    if (siteId && activeTab === 'roomManagement') {
      dispatch(
        fetchRooms({
          siteId,
          roomId: roomId,
        })
      );
    }
  }, [dispatch, siteId, activeTab, roomId]);

  useEffect(
    () => {
      dispatch(
        setBreadcrumb([
          { menu: siteName, link: `/${siteId}/information/${siteName}` },
        ])
      );
    },
    dispatch,
    siteId,
    siteName
  );

  const handleSearch = (e) => {
    setSearchError(null);
    const value = e.target.value;
    setSearch(value);
  };

  const formatDate = (date) => {
    const options = {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    };
    return date.toLocaleDateString('en-US', options);
  };

  const handlePrevDate = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentDate(newDate);
  };

  const handleNextDate = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentDate(newDate);
  };

  const handleDateChange = (date) => {
    if (date) {
      setCurrentDate(date.toDate());
    }
  };

  // Calculate date range for weekly calendar based on current date
  const getDateRange = () => {
    // For weekly view, ensure we get full Sunday to Saturday week
    const start = moment(currentDate).startOf('week'); // Sunday
    const end = moment(currentDate).endOf('week'); // Saturday
    return {
      start: start.format('YYYY-MM-DD'),
      end: end.format('YYYY-MM-DD'),
    };
  };

  // Find the current room details
  const currentRoom = rooms.find((room) => room._id === roomId);

  return (
    <div className="room-detail">
      <div className="media-header">
        <h1>{currentRoom.name}'s Schedule</h1>
        <Link
          to={`/${siteId}/information/${siteName}`}
          className="button-secondary"
        >
          <i className="feather-arrow-left" />
          Back
        </Link>
      </div>
      <div className="screens__nav">
        <div className="screens__search-wrapper">
          {/* Left side - Search */}
          <div className="left-side-controls">
            {/* Main search */}
            <div className="media-categories__search">
              <i className="feather-search" />
              <input
                type="text"
                placeholder="Search..."
                value={search}
                onChange={handleSearch}
              />
            </div>
            {searchError && <div className="search-error">{searchError}</div>}
          </div>

          {/* Right side controls */}
          <div className="right-side-controls">
            {/* Date Navigation */}
            <div className="date-navigation">
              <button className="nav-btn" onClick={handlePrevDate}>
                <i className="feather-chevron-left"></i>
              </button>

              <div className="date-display">
                <DatePicker
                  value={dayjs(currentDate)}
                  onChange={handleDateChange}
                  format="YYYY-MM-DD"
                  placeholder="Select date"
                  style={{ marginRight: '8px' }}
                />
                {dayjs(currentDate).isSame(dayjs(), 'day') && (
                  <div className="today-btn">Today</div>
                )}
              </div>

              <button className="nav-btn" onClick={handleNextDate}>
                <i className="feather-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>

        <div className="screens__button-group">
          {/* Add room specific buttons here */}
        </div>
      </div>

      <div className="room-detail-content">
        <VirtualCalendarWeekly
          dateRange={getDateRange()}
          search={search}
          selectedRoom={roomId}
          viewMode="weekly"
          siteId={siteId}
          rooms={rooms}
        />
      </div>
    </div>
  );
};

export default RoomDetail;
